import type { AxiosResponse } from 'axios'
import { supabase } from '@/supabase'
import { useAuthStore } from '@/stores/auth'

const AUTH_FORBIDDEN_DETAILS = new Set([
    'Invalid token or expired token.',
    'Invalid authentication scheme.',
])

/** True when the backend rejected the JWT (expired/invalid), not a permission 403. */
export function isAuthTokenForbiddenResponse(
    status: number | undefined,
    data: unknown,
): boolean {
    if (status !== 403) return false
    const detail =
        data && typeof data === 'object' && 'detail' in data
            ? (data as { detail?: unknown }).detail
            : undefined
    return typeof detail === 'string' && AUTH_FORBIDDEN_DETAILS.has(detail)
}

/**
 * True when validate_user failed (missing/revoked Supabase session, etc.).
 * Routers return 401 + { error: "Unauthorized" } in that case.
 */
export function isUnauthorizedAuthResponse(
    status: number | undefined,
    data: unknown,
): boolean {
    if (status !== 401) return false
    if (!data || typeof data !== 'object') return true
    const error =
        'error' in data ? (data as { error?: unknown }).error : undefined
    // Most auth failures use { error: "Unauthorized" }; accept bare 401s too.
    return error === undefined || error === 'Unauthorized'
}

let refreshInflight: Promise<string | null> | null = null

/** Refresh the Supabase session once; concurrent callers share the same promise. */
export async function refreshAccessToken(): Promise<string | null> {
    if (!refreshInflight) {
        refreshInflight = supabase.auth
            .refreshSession()
            .then(({ data, error }) => {
                if (error || !data.session?.access_token) return null
                return data.session.access_token
            })
            .catch(() => null)
            .finally(() => {
                refreshInflight = null
            })
    }
    return refreshInflight
}

let redirectingToLogin = false

/** Sign out and send the user to the login screen. Idempotent across concurrent 403s. */
export async function redirectToLoginAfterSessionExpired(): Promise<void> {
    if (redirectingToLogin) return
    redirectingToLogin = true

    try {
        await supabase.auth.signOut()
    } catch {
        // Best-effort; local session may already be gone.
    }

    try {
        useAuthStore().clearUser()
    } catch {
        // Pinia may not be ready in edge cases; redirect still clears UI state.
    }

    window.location.assign('/?login=true')
}

export function shouldRetryAuthRequest(config: { _authRetry?: boolean } | undefined): boolean {
    return !config?._authRetry
}

export function markAuthRequestRetried<T extends { _authRetry?: boolean }>(config: T): T {
    config._authRetry = true
    return config
}

export function getAuthForbiddenFromResponse(
    response: Pick<AxiosResponse, 'status' | 'data'> | undefined,
): boolean {
    if (!response) return false
    return (
        isAuthTokenForbiddenResponse(response.status, response.data)
        || isUnauthorizedAuthResponse(response.status, response.data)
    )
}
